import React, { useContext } from 'react';
import { Alert, Tag } from 'antd';
import { ColumnProps } from 'antd/lib/table';

import UbiquitiHostFirewallZonesContext from '@totem/components/ubiquiti/hosts/details/firewallZones/ubiquitiHostFirewallZonesContext';
import UbiquitiHostNetworksContext from '@totem/components/ubiquiti/hosts/details/networks/ubiquitiHostNetworksContext';
import {
  UbiquitiApiDataItem,
  UbiquitiFirewallZone,
} from '@totem/components/ubiquiti/types';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import IBTableExportButton from '@totem/components/ui_components/antDesignOverrides/IBTableExportButton';
import { isNotNull } from '@totem/utilities/common';
import { sortStringAscending } from '@totem/utilities/tableUtilities';

const UbiquitiHostFirewallZonesTable = () => {
  const { loading, error, firewallZones } = useContext(
    UbiquitiHostFirewallZonesContext,
  );
  const { networks } = useContext(UbiquitiHostNetworksContext);

  const getNetworkName = (networkId: string): string | null => {
    const foundNetwork = networks.find((chk) => chk.data.id === networkId);
    return isNotNull(foundNetwork) ? foundNetwork.data.name : null;
  };

  const columns: ColumnProps<UbiquitiApiDataItem<UbiquitiFirewallZone>>[] = [
    {
      title: 'Name',
      dataIndex: ['data', 'name'],
      key: 'name',
      sorter: (compA, compB) =>
        sortStringAscending(compA.data.name, compB.data.name),
    },
    {
      title: 'Origin',
      dataIndex: ['data', 'metadata', 'origin'],
      key: 'origin',
      render: (_, record: UbiquitiApiDataItem<UbiquitiFirewallZone>) => (
        <Tag>{record.data.metadata.origin}</Tag>
      ),
    },
    {
      title: 'Networks',
      dataIndex: ['data', 'networkIds'],
      key: 'networkIds',
      sorter: (compA, compB) =>
        compA.data.networkIds.length - compB.data.networkIds.length,
      render: (_, record: UbiquitiApiDataItem<UbiquitiFirewallZone>) =>
        record.data.networkIds.map((netId) => {
          const networkName = getNetworkName(netId);
          if (networkName === null) {
            return null;
          }

          return (
            <span key={netId}>
              <Tag>{networkName}</Tag>{' '}
            </span>
          );
        }),
    },
  ];

  const exportHeaders = ['Name', 'Origin', 'Networks'];
  const exportRows = firewallZones.map((zone) => [
    zone.data.name,
    zone.data.metadata.origin,
    zone.data.networkIds
      .map((netId) => getNetworkName(netId))
      .filter((name): name is string => name !== null)
      .join('; '),
  ]);

  if (error) {
    return (
      <Alert
        type="warning"
        showIcon
        message="Firewall zones unavailable"
        description="No firewall zone data could be found for this host."
      />
    );
  }

  return (
    <IBTable<UbiquitiApiDataItem<UbiquitiFirewallZone>>
      showSorterTooltip
      columns={columns}
      dataSource={firewallZones}
      loading={loading}
      pagination={{ defaultPageSize: 10, showSizeChanger: true }}
      rowKey={(record) => record.data.id}
      footerLeft={
        <IBTableExportButton
          fileName="firewall-zones"
          headers={exportHeaders}
          rows={exportRows}
        />
      }
    />
  );
};

export default UbiquitiHostFirewallZonesTable;
